(() => {
  'use strict';

  const root = document.querySelector('[data-iw-suite]');
  if (!root || root.dataset.bound === '1') return;
  root.dataset.bound = '1';

  const contextEl = document.querySelector('[data-iw-suite-context]');
  let ctx = {};
  try { ctx = JSON.parse(contextEl?.textContent || '{}'); } catch (_) { ctx = {}; }

  const shopRoot = ctx.root || '/';
  const currency = ctx.currency || 'USD';
  const KEYS = {
    wishlist: 'indiewrist_wishlist_v2',
    compare: 'indiewrist_compare_v2',
    recent: 'indiewrist_recent_v2'
  };
  const productCache = new Map();
  const catalogCache = { value: null };
  const finderSelection = new Set();

  const launcher = root.querySelector('[data-iw-suite-open]');
  const drawer = root.querySelector('[data-iw-suite-drawer]');
  const backdrop = root.querySelector('[data-iw-suite-backdrop]');
  const drawerContent = root.querySelector('[data-iw-suite-content]');
  const drawerTitle = root.querySelector('[data-iw-suite-title]');
  const savedCountEls = root.querySelectorAll('[data-iw-saved-count]');
  const compareCountEls = root.querySelectorAll('[data-iw-compare-count]');
  const modal = root.querySelector('[data-iw-suite-modal]');
  const modalBody = root.querySelector('[data-iw-suite-modal-body]');
  const toast = root.querySelector('[data-iw-suite-toast]');
  const sticky = root.querySelector('[data-iw-sticky]');
  let activeTab = 'wishlist';
  let toastTimer;

  const safeParse = (value, fallback = []) => {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : fallback;
    } catch (_) { return fallback; }
  };
  const read = key => {
    try { return safeParse(localStorage.getItem(KEYS[key]) || '[]'); }
    catch (_) { return []; }
  };
  const write = (key, items) => {
    try { localStorage.setItem(KEYS[key], JSON.stringify(items)); } catch (_) {}
    updateCounts();
  };
  const esc = value => String(value ?? '').replace(/[&<>"']/g, char => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;'
  }[char]));
  const money = cents => {
    const n = Number(cents);
    if (!Number.isFinite(n)) return '';
    try { return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(n / 100); }
    catch (_) { return `$${(n / 100).toFixed(2)}`; }
  };
  const moneyFromString = value => {
    const n = Number(value);
    if (!Number.isFinite(n)) return '';
    try { return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(n); }
    catch (_) { return `$${n.toFixed(2)}`; }
  };
  const handleFromUrl = href => {
    try {
      const url = new URL(href, window.location.origin);
      const match = url.pathname.match(/\/products\/([^/?#]+)/);
      return match ? decodeURIComponent(match[1]) : '';
    } catch (_) { return ''; }
  };
  const productUrl = handle => `${shopRoot}products/${encodeURIComponent(handle)}`;
  const isSaved = (key, handle) => read(key).some(item => item.handle === handle);

  const toastMessage = message => {
    if (!toast) return;
    clearTimeout(toastTimer);
    toast.textContent = message;
    toast.hidden = false;
    requestAnimationFrame(() => toast.classList.add('is-visible'));
    toastTimer = setTimeout(() => {
      toast.classList.remove('is-visible');
      setTimeout(() => { toast.hidden = true; }, 220);
    }, 2400);
  };

  const normalizeProduct = data => {
    if (!data) return null;
    const handle = data.handle || handleFromUrl(data.url || '');
    if (!handle) return null;
    const variants = Array.isArray(data.variants) ? data.variants : [];
    const firstAvailable = variants.find(v => v.available !== false) || variants[0] || {};
    let priceCents = Number(data.price);
    if (!Number.isFinite(priceCents)) {
      const p = Number(firstAvailable.price);
      priceCents = Number.isFinite(p) ? Math.round(p * (p < 1000 && String(firstAvailable.price).includes('.') ? 100 : 1)) : 0;
    }
    const image = data.featured_image || data.image || (Array.isArray(data.images) ? data.images[0] : null) || '';
    return {
      handle,
      title: data.title || handle,
      url: data.url || productUrl(handle),
      image: typeof image === 'object' ? (image.src || '') : image,
      priceCents,
      available: data.available !== false && (variants.length ? variants.some(v => v.available !== false) : true),
      variantId: firstAvailable.id || data.variantId || null
    };
  };

  const fetchProduct = async handle => {
    if (!handle) throw new Error('missing_handle');
    if (productCache.has(handle)) return productCache.get(handle);
    const promise = fetch(`${shopRoot}products/${encodeURIComponent(handle)}.js`, {
      headers: { Accept: 'application/json' }
    }).then(async response => {
      if (!response.ok) throw new Error('product_http');
      return response.json();
    });
    productCache.set(handle, promise);
    try { return await promise; }
    catch (error) { productCache.delete(handle); throw error; }
  };

  const loadCatalog = async () => {
    if (catalogCache.value) return catalogCache.value;
    const response = await fetch(`${shopRoot}products.json?limit=250`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('catalog_http');
    const data = await response.json();
    catalogCache.value = Array.isArray(data.products) ? data.products : [];
    return catalogCache.value;
  };

  const itemFromProductJs = product => {
    const normalized = normalizeProduct(product);
    if (!normalized) return null;
    return {
      ...normalized,
      url: productUrl(normalized.handle)
    };
  };

  const toggleStoreItem = async (key, handle) => {
    let items = read(key);
    const index = items.findIndex(item => item.handle === handle);
    if (index >= 0) {
      items.splice(index, 1);
      write(key, items);
      toastMessage(key === 'wishlist' ? 'Removed from Saved.' : 'Removed from Compare.');
      syncActionStates();
      if (!drawer.hidden && activeTab === key) renderTab(key);
      return false;
    }

    if (key === 'compare' && items.length >= 4) {
      toastMessage('Compare up to four watches at a time.');
      return false;
    }

    try {
      const product = await fetchProduct(handle);
      const item = itemFromProductJs(product);
      if (!item) throw new Error('normalize');
      items.unshift(item);
      write(key, items);
      toastMessage(key === 'wishlist' ? 'Saved to your edit.' : 'Added to Compare.');
      syncActionStates();
      if (!drawer.hidden && activeTab === key) renderTab(key);
      return true;
    } catch (_) {
      toastMessage('That watch could not be loaded. Please try again.');
      return false;
    }
  };

  const addRecent = item => {
    if (!item?.handle) return;
    let items = read('recent').filter(existing => existing.handle !== item.handle);
    items.unshift(item);
    write('recent', items.slice(0, 12));
  };

  const updateCounts = () => {
    const saved = read('wishlist').length;
    const compare = read('compare').length;
    savedCountEls.forEach(el => { el.textContent = String(saved); });
    compareCountEls.forEach(el => { el.textContent = String(compare); });
    if (launcher) {
      const total = saved + compare;
      launcher.classList.toggle('has-items', total > 0);
      launcher.setAttribute('aria-label', `My Edit. ${saved} saved, ${compare} to compare.`);
    }
  };

  if (ctx.currentProduct?.handle) {
    addRecent({
      handle: ctx.currentProduct.handle,
      title: ctx.currentProduct.title,
      url: ctx.currentProduct.url || productUrl(ctx.currentProduct.handle),
      image: ctx.currentProduct.image || '',
      priceCents: Number(ctx.currentProduct.priceCents) || 0,
      available: ctx.currentProduct.available !== false,
      variantId: ctx.currentProduct.variantId || null
    });
  }

  const cardMarkup = (item, key) => `
    <article class="iw-suite-item">
      <a class="iw-suite-item__image" href="${esc(item.url || productUrl(item.handle))}">
        ${item.image ? `<img src="${esc(item.image)}" alt="${esc(item.title)}" loading="lazy">` : '<span class="iw-suite-item__placeholder">INDIEWRIST</span>'}
      </a>
      <div class="iw-suite-item__copy">
        <p class="iw-suite-item__eyebrow">${key === 'recent' ? 'RECENTLY VIEWED' : key === 'compare' ? 'COMPARE' : 'SAVED'}</p>
        <h4><a href="${esc(item.url || productUrl(item.handle))}">${esc(item.title)}</a></h4>
        <p class="iw-suite-item__price">${money(item.priceCents)}</p>
        <div class="iw-suite-item__actions">
          <button type="button" data-iw-action="quick" data-handle="${esc(item.handle)}">Quick view</button>
          ${key !== 'recent' ? `<button type="button" data-iw-remove="${esc(key)}" data-handle="${esc(item.handle)}">Remove</button>` : ''}
        </div>
      </div>
    </article>`;

  const emptyMarkup = (title, text) => `
    <div class="iw-suite-empty">
      <span>◌</span>
      <h3>${esc(title)}</h3>
      <p>${esc(text)}</p>
      <a href="${esc(shopRoot)}collections/all">Explore all watches →</a>
    </div>`;

  const renderList = key => {
    const items = read(key);
    const labels = {
      wishlist: ['Nothing saved yet.', 'Tap the heart on any watch and it will stay here on this device.'],
      compare: ['Your compare list is empty.', 'Add up to four watches and line them up side by side.'],
      recent: ['No recently viewed watches yet.', 'As you browse, the watches you open will appear here.']
    };
    if (!items.length) return emptyMarkup(labels[key][0], labels[key][1]);

    let html = `<div class="iw-suite-list">${items.map(item => cardMarkup(item, key)).join('')}</div>`;
    if (key === 'compare') {
      html += `
        <div class="iw-compare-summary">
          <p class="iw-suite-kicker">SIDE BY SIDE</p>
          <div class="iw-compare-summary__grid">
            ${items.map(item => `
              <div>
                <strong>${esc(item.title)}</strong>
                <span>${money(item.priceCents)}</span>
                <small>${item.available === false ? 'Currently unavailable' : 'Available'}</small>
              </div>`).join('')}
          </div>
          <p class="iw-compare-note">Open each watch for full specifications and options. We only compare details published by the store.</p>
        </div>`;
    }
    return html;
  };

  const FINDER_GROUPS = {
    petite: ['petite', 'mini', 'small', 'dainty', 'slim', 'micro'],
    leather: ['leather', 'strap'],
    bracelet: ['bracelet', 'steel', 'chain', 'bangle'],
    square: ['square', 'rectangle', 'rectangular', 'tank'],
    soft: ['round', 'oval', 'cushion'],
    sculptural: ['tonneau', 'barrel', 'sculptural', 'arch', 'clover'],
    jewelry: ['jewelry', 'jewellery', 'crystal', 'pave', 'pavé', 'cocktail', 'mother of pearl', 'mother-of-pearl'],
    vintage: ['vintage', 'retro', 'french', 'classic'],
    statement: ['statement', 'cuff', 'rainbow', 'crystal', 'sculptural', 'pendant']
  };

  const finderMarkup = () => `
    <div class="iw-finder-mini">
      <p class="iw-suite-kicker">INSTANT WATCH FINDER</p>
      <h3>Build a quick edit.</h3>
      <p>Choose up to three directions and we’ll pull the closest matches from the watches currently published in the store.</p>
      <div class="iw-finder-mini__chips">
        ${[
          ['petite', 'Petite'], ['leather', 'Leather'], ['bracelet', 'Bracelet'],
          ['square', 'Square / Tank'], ['soft', 'Round / Oval'], ['sculptural', 'Tonneau / Sculptural'],
          ['jewelry', 'Jewelry-like'], ['vintage', 'Vintage mood'], ['statement', 'Statement']
        ].map(([value, label]) => `<button type="button" data-iw-finder-chip="${value}" aria-pressed="${finderSelection.has(value)}">${label}</button>`).join('')}
      </div>
      <button class="iw-suite-primary" type="button" data-iw-finder-go>Show my edit <span>→</span></button>
      <div data-iw-finder-results></div>
    </div>`;

  const finderResults = async () => {
    const target = root.querySelector('[data-iw-finder-results]');
    if (!target) return;
    if (!finderSelection.size) {
      target.innerHTML = '<p class="iw-finder-mini__hint">Choose at least one style direction first.</p>';
      return;
    }
    target.innerHTML = '<p class="iw-finder-mini__loading">Building your edit…</p>';
    try {
      const catalog = await loadCatalog();
      const selected = [...finderSelection];
      const scored = catalog.map((product, index) => {
        const meta = [
          product.title,
          product.product_type,
          Array.isArray(product.tags) ? product.tags.join(' ') : product.tags
        ].filter(Boolean).join(' ').toLowerCase();
        let score = 0;
        selected.forEach(group => {
          const terms = FINDER_GROUPS[group] || [];
          const hits = terms.filter(term => meta.includes(term.toLowerCase())).length;
          if (hits) score += 4 + Math.min(3, hits - 1);
        });
        return { product, index, score };
      }).filter(row => row.score > 0).sort((a, b) => b.score - a.score || a.index - b.index).slice(0, 6);

      const fallback = scored.length ? scored : catalog.slice(0, 6).map((product, index) => ({ product, index, score: 0 }));
      target.innerHTML = `
        <div class="iw-finder-mini__results">
          ${fallback.map(({ product }) => {
            const first = (product.variants || []).find(v => v.available !== false) || (product.variants || [])[0] || {};
            const image = (product.images || [])[0] || '';
            return `
              <article>
                <a href="${esc(productUrl(product.handle))}">
                  ${image ? `<img src="${esc(image)}" alt="${esc(product.title)}" loading="lazy">` : ''}
                </a>
                <div>
                  <h4><a href="${esc(productUrl(product.handle))}">${esc(product.title)}</a></h4>
                  <p>${moneyFromString(first.price)}</p>
                  <button type="button" data-iw-action="quick" data-handle="${esc(product.handle)}">Quick view</button>
                </div>
              </article>`;
          }).join('')}
        </div>`;
    } catch (_) {
      target.innerHTML = '<p class="iw-finder-mini__hint">The finder could not load right now. You can still browse all watches.</p>';
    }
  };

  const renderTab = key => {
    activeTab = key;
    root.querySelectorAll('[data-iw-tab]').forEach(button => {
      const active = button.dataset.iwTab === key;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-selected', String(active));
    });
    const titles = { wishlist: 'Saved watches', compare: 'Compare watches', recent: 'Recently viewed', finder: 'Find my watch' };
    if (drawerTitle) drawerTitle.textContent = titles[key] || 'My Edit';
    drawerContent.innerHTML = key === 'finder' ? finderMarkup() : renderList(key);
  };

  const openDrawer = (tab = activeTab) => {
    renderTab(tab);
    drawer.hidden = false;
    backdrop.hidden = false;
    root.classList.add('is-open');
    document.body.classList.add('iw-suite-lock');
    launcher?.setAttribute('aria-expanded', 'true');
    setTimeout(() => root.querySelector('[data-iw-suite-close]')?.focus(), 30);
  };
  const closeDrawer = () => {
    drawer.hidden = true;
    backdrop.hidden = true;
    root.classList.remove('is-open');
    document.body.classList.remove('iw-suite-lock');
    launcher?.setAttribute('aria-expanded', 'false');
  };

  const showModal = html => {
    modalBody.innerHTML = html;
    modal.hidden = false;
    root.classList.add('modal-open');
    document.body.classList.add('iw-suite-lock');
    requestAnimationFrame(() => modal.classList.add('is-visible'));
  };
  const closeModal = () => {
    modal.classList.remove('is-visible');
    setTimeout(() => {
      modal.hidden = true;
      modalBody.innerHTML = '';
      root.classList.remove('modal-open');
      if (drawer.hidden) document.body.classList.remove('iw-suite-lock');
    }, 180);
  };

  const quickView = async handle => {
    showModal('<div class="iw-qv-loading">Loading the watch…</div>');
    try {
      const product = await fetchProduct(handle);
      const item = itemFromProductJs(product);
      if (!item) throw new Error('normalize');
      addRecent(item);
      const variants = Array.isArray(product.variants) ? product.variants : [];
      const available = variants.filter(v => v.available !== false);
      const selected = available[0] || variants[0];
      const image = product.featured_image || (product.images || [])[0] || '';
      const description = String(product.description || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 240);
      modalBody.innerHTML = `
        <div class="iw-qv" data-iw-qv data-handle="${esc(handle)}">
          <div class="iw-qv__media">${image ? `<img src="${esc(image)}" alt="${esc(product.title)}">` : '<div class="iw-qv__placeholder">INDIEWRIST</div>'}</div>
          <div class="iw-qv__copy">
            <p class="iw-suite-kicker">QUICK VIEW</p>
            <h2>${esc(product.title)}</h2>
            <p class="iw-qv__price" data-iw-qv-price>${selected ? money(Number(selected.price)) : money(Number(product.price))}</p>
            ${description ? `<p class="iw-qv__description">${esc(description)}${String(product.description || '').length > 240 ? '…' : ''}</p>` : ''}
            ${variants.length > 1 && variants.some(v => v.title !== 'Default Title') ? `
              <label class="iw-qv__label">Choose an option
                <select data-iw-qv-variant>
                  ${variants.map(v => `<option value="${esc(v.id)}" data-price="${esc(v.price)}" ${v.available === false ? 'disabled' : ''}>${esc(v.title)}${v.available === false ? ' — Sold out' : ''}</option>`).join('')}
                </select>
              </label>` : ''}
            <div class="iw-qv__main-actions">
              <button class="iw-suite-primary" type="button" data-iw-qv-add data-variant="${esc(selected?.id || '')}" ${selected?.available === false || !selected ? 'disabled' : ''}>${selected?.available === false || !selected ? 'Sold out' : 'Add to bag'}</button>
              <a class="iw-qv__full" href="${esc(productUrl(handle))}">Full details →</a>
            </div>
            <div class="iw-qv__secondary">
              <button type="button" data-iw-action="wishlist" data-handle="${esc(handle)}">${isSaved('wishlist', handle) ? '♥ Saved' : '♡ Save'}</button>
              <button type="button" data-iw-action="compare" data-handle="${esc(handle)}">${isSaved('compare', handle) ? '✓ Comparing' : '⇄ Compare'}</button>
            </div>
          </div>
        </div>`;
    } catch (_) {
      modalBody.innerHTML = `
        <div class="iw-qv-error">
          <p class="iw-suite-kicker">QUICK VIEW</p>
          <h2>We couldn’t load this watch.</h2>
          <p>Please open the product page and try again.</p>
          <a href="${esc(productUrl(handle))}">View watch →</a>
        </div>`;
    }
  };

  const addToCart = async (variantId, button) => {
    if (!variantId) return;
    const original = button?.textContent || 'Add to bag';
    if (button) { button.disabled = true; button.textContent = 'Adding…'; }
    try {
      const response = await fetch(`${shopRoot}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
      });
      if (!response.ok) throw new Error('cart');
      toastMessage('Added to bag.');
      if (button) button.textContent = 'Added ✓';
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      setTimeout(() => { if (button) { button.disabled = false; button.textContent = original; } }, 1400);
    } catch (_) {
      toastMessage('Could not add that option. Please try again.');
      if (button) { button.disabled = false; button.textContent = original; }
    }
  };

  const syncActionStates = () => {
    document.querySelectorAll('[data-iw-action="wishlist"][data-handle]').forEach(button => {
      const on = isSaved('wishlist', button.dataset.handle);
      button.classList.toggle('is-active', on);
      if (button.classList.contains('iw-card-tool--save')) button.setAttribute('aria-label', on ? 'Remove from Saved' : 'Save watch');
    });
    document.querySelectorAll('[data-iw-action="compare"][data-handle]').forEach(button => {
      const on = isSaved('compare', button.dataset.handle);
      button.classList.toggle('is-active', on);
      if (button.classList.contains('iw-card-tool--compare')) button.setAttribute('aria-label', on ? 'Remove from Compare' : 'Compare watch');
    });
  };

  const injectCardTools = () => {
    document.querySelectorAll('#MainContent a[href*="/products/"]').forEach(link => {
      const handle = handleFromUrl(link.href);
      if (!handle || !link.querySelector('img')) return;
      const card = link.closest('product-card, .product-card, .card-wrapper, .card, article, li');
      if (!card || card.dataset.iwToolsBound === '1' || card.closest('[data-iw-suite]')) return;
      card.dataset.iwToolsBound = '1';
      card.classList.add('iw-suite-card');
      const tools = document.createElement('div');
      tools.className = 'iw-card-tools';
      tools.innerHTML = `
        <button class="iw-card-tool iw-card-tool--save" type="button" data-iw-action="wishlist" data-handle="${esc(handle)}" aria-label="Save watch" title="Save">♡</button>
        <button class="iw-card-tool iw-card-tool--compare" type="button" data-iw-action="compare" data-handle="${esc(handle)}" aria-label="Compare watch" title="Compare">⇄</button>
        <button class="iw-card-tool iw-card-tool--quick" type="button" data-iw-action="quick" data-handle="${esc(handle)}">Quick view</button>`;
      card.appendChild(tools);
    });
    syncActionStates();
  };

  const injectProductPageActions = () => {
    if (!ctx.currentProduct?.handle) return;
    const form = [...document.querySelectorAll('form[action*="/cart/add"]')].find(candidate =>
      candidate.querySelector('[name="id"]') || candidate.querySelector('button[type="submit"]')
    );
    if (!form || document.querySelector('[data-iw-product-actions]')) return;
    const actions = document.createElement('div');
    actions.className = 'iw-product-actions';
    actions.dataset.iwProductActions = '1';
    actions.innerHTML = `
      <button type="button" data-iw-action="wishlist" data-handle="${esc(ctx.currentProduct.handle)}">♡ Save for later</button>
      <button type="button" data-iw-action="compare" data-handle="${esc(ctx.currentProduct.handle)}">⇄ Compare</button>`;
    form.insertAdjacentElement('beforebegin', actions);
    syncActionStates();
  };

  const setupSticky = () => {
    if (!sticky || !ctx.currentProduct?.handle) return;
    const form = [...document.querySelectorAll('form[action*="/cart/add"]')].find(candidate =>
      candidate.querySelector('[name="id"]') || candidate.querySelector('button[type="submit"]')
    );
    const stickyButton = sticky.querySelector('[data-iw-sticky-add]');
    if (!stickyButton) return;

    const currentVariantId = () => {
      const input = form?.querySelector('[name="id"]:checked') || form?.querySelector('select[name="id"]') || form?.querySelector('input[name="id"]');
      return input?.value || ctx.currentProduct.variantId || '';
    };

    stickyButton.addEventListener('click', event => {
      event.preventDefault();
      const submit = form?.querySelector('button[type="submit"]:not([disabled]), input[type="submit"]:not([disabled])');
      if (submit) {
        submit.click();
        return;
      }
      addToCart(currentVariantId(), stickyButton);
    });

    if (!form) {
      sticky.hidden = false;
      document.body.classList.add('iw-sticky-active');
      return;
    }

    const setVisible = show => {
      sticky.hidden = !show;
      document.body.classList.toggle('iw-sticky-active', show);
    };
    setVisible(false);

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(entries => {
        const entry = entries[0];
        setVisible(!entry.isIntersecting && window.scrollY > 280);
      }, { threshold: 0.05 });
      observer.observe(form);
    } else {
      window.addEventListener('scroll', () => {
        const rect = form.getBoundingClientRect();
        setVisible(rect.bottom < 0 && window.scrollY > 280);
      }, { passive: true });
    }
  };

  launcher?.addEventListener('click', () => openDrawer('wishlist'));
  root.addEventListener('click', async event => {
    const close = event.target.closest('[data-iw-suite-close]');
    if (close) { closeDrawer(); return; }
    if (event.target === backdrop) { closeDrawer(); return; }
    const modalClose = event.target.closest('[data-iw-modal-close]');
    if (modalClose || event.target.matches('[data-iw-modal-backdrop]')) { closeModal(); return; }

    const tab = event.target.closest('[data-iw-tab]');
    if (tab) { renderTab(tab.dataset.iwTab); return; }

    const remove = event.target.closest('[data-iw-remove]');
    if (remove) {
      const key = remove.dataset.iwRemove;
      const handle = remove.dataset.handle;
      write(key, read(key).filter(item => item.handle !== handle));
      renderTab(key);
      syncActionStates();
      return;
    }

    const chip = event.target.closest('[data-iw-finder-chip]');
    if (chip) {
      const value = chip.dataset.iwFinderChip;
      if (finderSelection.has(value)) finderSelection.delete(value);
      else if (finderSelection.size < 3) finderSelection.add(value);
      else toastMessage('Choose up to three directions.');
      chip.setAttribute('aria-pressed', String(finderSelection.has(value)));
      chip.classList.toggle('is-active', finderSelection.has(value));
      return;
    }

    if (event.target.closest('[data-iw-finder-go]')) { finderResults(); return; }

    const action = event.target.closest('[data-iw-action]');
    if (action) {
      event.preventDefault();
      event.stopPropagation();
      const type = action.dataset.iwAction;
      const handle = action.dataset.handle;
      if (type === 'quick') { quickView(handle); return; }
      if (type === 'wishlist' || type === 'compare') { await toggleStoreItem(type, handle); return; }
    }

    const qvVariant = event.target.closest('[data-iw-qv-variant]');
    if (qvVariant) return;

    const qvAdd = event.target.closest('[data-iw-qv-add]');
    if (qvAdd) {
      const qv = qvAdd.closest('[data-iw-qv]');
      const select = qv?.querySelector('[data-iw-qv-variant]');
      const variantId = select?.value || qvAdd.dataset.variant;
      addToCart(variantId, qvAdd);
    }
  });

  root.addEventListener('change', event => {
    const select = event.target.closest('[data-iw-qv-variant]');
    if (!select) return;
    const option = select.options[select.selectedIndex];
    const qv = select.closest('[data-iw-qv]');
    const add = qv?.querySelector('[data-iw-qv-add]');
    const price = qv?.querySelector('[data-iw-qv-price]');
    if (add) add.dataset.variant = select.value;
    if (price && option?.dataset.price) price.textContent = money(Number(option.dataset.price));
  });

  document.addEventListener('click', async event => {
    const action = event.target.closest('[data-iw-action]');
    if (!action || action.closest('[data-iw-suite]')) return;
    event.preventDefault();
    event.stopPropagation();
    const type = action.dataset.iwAction;
    const handle = action.dataset.handle;
    if (type === 'quick') quickView(handle);
    else if (type === 'wishlist' || type === 'compare') await toggleStoreItem(type, handle);
  }, true);

  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    if (!modal.hidden) closeModal();
    else if (!drawer.hidden) closeDrawer();
  });

  let scanTimer;
  const scheduleScan = () => {
    clearTimeout(scanTimer);
    scanTimer = setTimeout(() => {
      injectCardTools();
      injectProductPageActions();
    }, 120);
  };
  const main = document.querySelector('#MainContent');
  if (main && 'MutationObserver' in window) {
    new MutationObserver(scheduleScan).observe(main, { childList: true, subtree: true });
  }

  updateCounts();
  injectCardTools();
  injectProductPageActions();
  setupSticky();
})();